import { useEffect, useMemo, useRef } from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    type DataTableMode,
    type DataTablePagination,
    type DataTableSort
} from '@odo/shared-ui';
import type { IteroPatient } from '../../../../services/iteroBrowse';
import { BROWSE_TABLE_CARD_CLASS, SELECTED_ROW_CLASS, TABLE_ROW_HEIGHT } from './browserStyles';
import { buildPatientColumns, patientSkeletonWidths } from './browseColumns';
import type { TableLayout } from './paneVisibility';
import { formatPatientName } from './iteroBrowseFormat';
import type { PatientSortKey } from './patientSort';
import { TableEmpty, TableError, TableSkeletonRows } from './TableStates';

function patientRowLabel(patient: IteroPatient, isSelected: boolean): string {
    const name = formatPatientName(patient.lastName, patient.firstName);
    const chart = patient.chartNumber ? `, chart ${patient.chartNumber}` : '';
    return `${name}${chart}${isSelected ? ', selected' : ''}`;
}

interface IteroPatientsTableProps {
    rows: readonly IteroPatient[];
    layout: TableLayout;
    isLoading: boolean;
    isUpdating: boolean;
    errorMessage: string | null;
    hasFilters: boolean;
    selectedId: string | null;
    sort: DataTableSort<PatientSortKey>;
    listKey: string;
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    pagination: DataTablePagination;
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    onSelect: (patient: IteroPatient) => void;
    onClearFilters: () => void;
    onRetry: () => void;
}

export function IteroPatientsTable({
    rows,
    layout,
    isLoading,
    isUpdating,
    errorMessage,
    hasFilters,
    selectedId,
    sort,
    listKey,
    mode,
    onModeChange,
    pagination,
    hasMore,
    isLoadingMore,
    onLoadMore,
    onSelect,
    onClearFilters,
    onRetry
}: IteroPatientsTableProps) {
    const columns = useMemo(() => buildPatientColumns(layout), [layout]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const refs = useMemo(() => ({ scroll: scrollRef }), []);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 0 });
    }, [listKey]);

    return (
        <DataTable
            label="iTero patients"
            columns={columns}
            rows={rows}
            getRowKey={(patient) => patient.id}
            rowAction={{
                onActivate: onSelect,
                getLabel: (patient) => patientRowLabel(patient, patient.id === selectedId)
            }}
            rowClassName={(patient) => (patient.id === selectedId ? SELECTED_ROW_CLASS : undefined)}
            rowHeightClassName={TABLE_ROW_HEIGHT}
            sort={sort}
            mode={mode}
            onModeChange={onModeChange}
            pagination={pagination}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={onLoadMore}
            loadMoreLabel="Load more patients"
            isLoading={isLoading}
            loadingRows={<TableSkeletonRows widths={patientSkeletonWidths(layout)} />}
            emptyState={
                errorMessage ? (
                    <TableError message={errorMessage} onRetry={onRetry} />
                ) : (
                    <TableEmpty
                        icon={UsersIcon}
                        message={
                            hasFilters
                                ? 'No iTero patients match these filters.'
                                : 'This clinic has no iTero patients yet.'
                        }
                        onClear={hasFilters ? onClearFilters : undefined}
                    />
                )
            }
            emptyStateCellClassName="px-3 py-16"
            className="min-h-0"
            cardClassName={BROWSE_TABLE_CARD_CLASS}
            scrollClassName={isUpdating ? 'opacity-60 transition-opacity' : 'transition-opacity'}
            refs={refs}
        />
    );
}
